/**
 * Phase 185 — Universal Document Download Helper
 * -------------------------------------------------------
 * Every "PDF" button in the platform funnels through this helper.  It
 * understands two backend response modes:
 *
 *   1.  `application/pdf`  → standard download as a binary attachment.
 *   2.  `text/html` with `X-Pdf-Fallback: html` → opens the recovery
 *       print-view in a new tab so the user gets the same document with
 *       a sticky "Print as PDF" banner.  No dark-red toast.
 *
 * Defensive layer: if the FIRST request fails with 5xx, we transparently
 * re-fire the same URL with `?format=html` and open it in a new tab.
 *
 * ──────────────────────────────────────────────────────────────────────
 * Production bugfix (June 2026) — Navigation hijack on HTML fallback:
 *
 * Calling `window.open()` AFTER `await apiClient.get(...)` loses the
 * trusted-user-gesture context in modern browsers (Chrome, Safari,
 * Brave). The popup blocker silently returns `null`, and the old
 * `window.location.href = url` fallback would then hijack the CURRENT
 * tab — kicking the user out of the app.
 *
 * Fix: pre-open the blank tab SYNCHRONOUSLY at the start of
 * `downloadDocument()` so the gesture is still trusted. Pass the
 * handle through, and write the blob URL into it when ready. If the
 * pre-open is blocked anyway, fall back to downloading the HTML as an
 * attachment — never navigate the current tab.
 * ──────────────────────────────────────────────────────────────────────
 *
 * Usage:
 *   await downloadDocument(api, `/payslips/${id}/download`,
 *                          `Payslip_${name}_${month}.pdf`,
 *                          { label: 'Payslip' });
 */
import { toast } from 'sonner';

const PDF_DEFAULT_LABEL = 'Document';

/**
 * Force-trigger a binary file download from a Blob.
 */
function _triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

/**
 * Try to point a pre-opened tab at a freshly-baked blob URL. If no
 * pre-opened tab is available (popup blocked or never opened), fall
 * back to downloading the HTML as a `.html` attachment so the user
 * can still print it — and CRUCIALLY never navigate the current tab.
 */
function _renderHtmlIntoTab(prefetchedWin, blob, label, filename) {
  const url = window.URL.createObjectURL(blob);

  if (prefetchedWin && !prefetchedWin.closed) {
    try {
      prefetchedWin.location.href = url;
      // Phase 264 — Strip opener AFTER navigation; this preserves the
      // security intent of `noopener` (which we couldn't pass at open-time
      // without forfeiting the window handle).
      try { prefetchedWin.opener = null; } catch (_e) { /* noop */ }
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
      toast.success(
        `${label || PDF_DEFAULT_LABEL} opened as printable HTML`,
        {
          description:
            'Press Ctrl+P / ⌘P to save as PDF — or use the green Print button at the top of the tab.',
          duration: 6000,
        },
      );
      return { mode: 'new-tab' };
    } catch (e) {
      // Pre-opened window was lost (rare cross-origin edge) — fall through
    }
  }

  // No usable pre-opened tab → download as attachment.  NEVER hijack
  // the current tab via window.location.href.
  const fallbackName = (filename || `${label || 'document'}.html`).replace(
    /\.pdf$/i,
    '.html',
  );
  _triggerBlobDownload(blob, fallbackName);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  toast.success(
    `${label || PDF_DEFAULT_LABEL} downloaded as HTML`,
    {
      description:
        'Open the file and press Ctrl+P / ⌘P to save as PDF. Allow popups for this site to skip this step next time.',
      duration: 7000,
    },
  );
  return { mode: 'html-download' };
}

/**
 * Best-effort download with transparent PDF→HTML fallback.
 *
 * @param {object}  apiClient        Axios instance (typically `import api from '@/lib/api'`)
 * @param {string}  apiPath          Backend path relative to /api (e.g. `/payslips/abc/download`)
 * @param {string}  filename         Suggested download filename for PDFs
 * @param {object}  [opts]
 * @param {string}  [opts.label]     Friendly document label for the toast (e.g. 'Payslip')
 * @param {boolean} [opts.silent]    Don't show the "Generating PDF..." toast
 */
export async function downloadDocument(apiClient, apiPath, filename, opts = {}) {
  const label = opts.label || PDF_DEFAULT_LABEL;

  // ── CRITICAL: pre-open a blank tab synchronously, while we still
  // hold the trusted user-gesture context. We may close it again if
  // the response turns out to be a PDF (binary download path). ─────
  let prefetchedWin = null;
  try {
    // Phase 264 — DO NOT pass `noopener,noreferrer` here. When `noopener` is
    // set, browsers intentionally return `null` from `window.open()` and the
    // pre-opened tab is unrecoverable — it stays stuck on about:blank
    // forever because we can't write to its document. We strip
    // `window.opener` later to retain the security intent.
    prefetchedWin = window.open('about:blank', '_blank');
    if (prefetchedWin && prefetchedWin.document) {
      // Friendly placeholder while we fetch the document.
      prefetchedWin.document.write(
        `<!doctype html><html><head><title>Loading ${label}…</title>` +
        `<meta charset="utf-8" /></head>` +
        `<body style="font-family:-apple-system,Segoe UI,sans-serif;` +
        `padding:48px;color:#475569;background:#f8fafc">` +
        `<h2 style="color:#0f172a;margin:0 0 8px 0">Preparing your ${label.toLowerCase()}…</h2>` +
        `<p style="margin:0">This tab will auto-refresh once it's ready.</p>` +
        `</body></html>`,
      );
    }
  } catch (e) {
    prefetchedWin = null;
  }
  const _closePreOpenedTab = () => {
    if (prefetchedWin && !prefetchedWin.closed) {
      try { prefetchedWin.close(); } catch (e) { /* noop */ }
    }
  };

  let progressToast = null;
  if (!opts.silent) {
    try {
      progressToast = toast.loading(`Generating ${label}...`);
    } catch (e) { /* sonner unavailable */ }
  }

  const _dismiss = () => {
    if (progressToast != null) {
      try { toast.dismiss(progressToast); } catch (e) { /* noop */ }
    }
  };

  try {
    const r = await apiClient.get(apiPath, { responseType: 'blob' });
    const ct = (r.headers?.['content-type'] || '').toLowerCase();
    const fallbackHeader = (r.headers?.['x-pdf-fallback'] || '').toLowerCase();

    if (ct.startsWith('application/pdf')) {
      const blob = new Blob([r.data], { type: 'application/pdf' });
      // PDF path doesn't need the pre-opened tab — close it cleanly.
      _closePreOpenedTab();
      _triggerBlobDownload(blob, filename);
      _dismiss();
      try { toast.success(`${label} downloaded`); } catch (e) { /* noop */ }
      return { ok: true, mode: 'pdf' };
    }

    // Backend transparently served the HTML print view (Phase 185 fallback).
    if (fallbackHeader === 'html' || ct.startsWith('text/html')) {
      const htmlBlob = new Blob([r.data], { type: 'text/html' });
      const res = _renderHtmlIntoTab(prefetchedWin, htmlBlob, label, filename);
      // If we attachment-fell-back, the pre-opened tab is now showing
      // the loading placeholder forever — close it.
      if (res.mode === 'html-download') _closePreOpenedTab();
      _dismiss();
      return { ok: true, mode: 'html-fallback' };
    }

    // Unknown content-type — surface a soft error without dark-red doom.
    _closePreOpenedTab();
    _dismiss();
    try { toast.error(`Unexpected ${label.toLowerCase()} response from server`); } catch (e) { /* noop */ }
    return { ok: false, mode: 'unknown', reason: ct };

  } catch (err) {
    const status = err?.response?.status;
    // 5xx → backend hard-failed.  Fire the defensive `?format=html` retry.
    if (status && status >= 500 && status < 600) {
      try {
        const retryPath = apiPath + (apiPath.includes('?') ? '&' : '?') + 'format=html';
        const r2 = await apiClient.get(retryPath, { responseType: 'blob' });
        const htmlBlob = new Blob([r2.data], { type: 'text/html' });
        const res = _renderHtmlIntoTab(prefetchedWin, htmlBlob, label, filename);
        if (res.mode === 'html-download') _closePreOpenedTab();
        _dismiss();
        return { ok: true, mode: 'html-retry' };
      } catch (e2) {
        _closePreOpenedTab();
        _dismiss();
        try {
          toast.error(`${label} download failed`, {
            description: 'Please retry in a moment. If it keeps failing, contact support.',
          });
        } catch (e) { /* noop */ }
        return { ok: false, mode: 'failed', reason: e2 };
      }
    }

    // Non-5xx — show server's detail message if available
    _closePreOpenedTab();
    let detail = err?.response?.data?.detail;
    if (!detail && err?.response?.data instanceof Blob) {
      try {
        const txt = await err.response.data.text();
        try { detail = JSON.parse(txt)?.detail; } catch (e) { /* noop */ }
      } catch (e) { /* noop */ }
    }
    _dismiss();
    try {
      toast.error(detail || `${label} download failed`);
    } catch (e) { /* noop */ }
    return { ok: false, mode: 'failed', reason: detail || err?.message };
  }
}

export default downloadDocument;
